import { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getCurrencyRate } from "../../api/currency";
import arrow from "../../images/icons/cont_arrow.svg";
import loaderIcon from "../../images/icons/loader.svg";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { date, currency } = payload[0].payload;
    const formattedDate = new Date(date).toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    return (
      <div className="custom-tooltip">
        <p className="tooltip-date">{formattedDate}</p>
        <p className="tooltip-value">{currency} ₽</p>
      </div>
    );
  }
  return null;
};

const RateHistory = () => {
  const [rates, setRates] = useState([]);
  const [limit, setLimit] = useState(7);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedRate, setSelectedRate] = useState(null);
  const [loadingToday, setLoadingToday] = useState(false);
  const [loadingPeriod, setLoadingPeriod] = useState(false);
  const [minRate, setMinRate] = useState(0);
  const [maxRate, setMaxRate] = useState(0);
  const [ticks, setTicks] = useState(null);

  // Получение курса за период
  const fetchRatesForPeriod = async ({ limit = 7 }) => {
    try {
      const data = {
        date: null,
        limit: limit,
      };

      const response = await getCurrencyRate(data);

      if (response.result && response.result.length > 0)
        return { count: response.count, result: response.result };
      else return { error: "Failed to fetch rates" };
    } catch (error) {
      // console.error("Error fetching rates: ", error);
    }
  };

  // Получение курса на определенную дату
  const fetchRateByDate = async (date) => {
    try {
      const data = {
        date: date,
      };

      const response = await getCurrencyRate(data);

      if (response.result && response.result.length === 1)
        return {
          date: response.result[0].date,
          currency: response.result[0].currency,
        };
      else return { error: "Failed to fetch rate" };
    } catch (error) {
      // console.error("Error fetching rate by date: ", error);
    }
  };

  // Вычисление значений для оси Y
  const calculateYAxis = (data, tickTargetCount = 5) => {
    if (!data || data.length === 0) {
      return { min: 0, max: 0, ticks: [] };
    }

    const values = data.map((item) => item.currency);
    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);

    let range = maxVal - minVal;
    if (range === 0) {
      range = 0.1;
    }

    const padding = range * 0.1;
    let newMin = minVal - padding;
    let newMax = maxVal + padding;

    const fullRange = newMax - newMin;

    // Вычислем шаг, чтобы тиков было ровно tickTargetCount
    let rawStep = fullRange / (tickTargetCount - 1);

    // Округление шаг до красивого числа
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const niceSteps = [1, 2, 2.5, 5, 10, 0.5, 0.2, 0.1, 0.05];
    let tickStep =
      niceSteps.find((s) => s * magnitude >= rawStep) * magnitude || rawStep;

    // Вычисление границ, чтобы вместились ровно tickTargetCount тиков
    newMin = Math.floor(newMin / tickStep) * tickStep;
    newMax = newMin + tickStep * (tickTargetCount - 1);

    const ticks = [];
    for (let i = 0; i < tickTargetCount; i++) {
      ticks.push(+(newMin + i * tickStep).toFixed(2));
    }

    return { min: newMin, max: newMax, ticks };
  };

  // Загрузка данных для графика
  const loadRates = async () => {
    setLoadingPeriod(true);
    try {
      const data = await fetchRatesForPeriod({ limit: 365 }); // всегда грузим максимум, фильтруем дальше сами

      if (data && data.result) {
        const now = new Date();
        let daysBack = limit; // 7, 30 или 365
        const filtered = data.result.filter((item) => {
          const itemDate = new Date(item.date);
          const diffDays = Math.floor((now - itemDate) / (1000 * 60 * 60 * 24));
          return diffDays <= daysBack;
        });

        filtered.sort((a, b) => new Date(a.date) - new Date(b.date));

        const { min, max, ticks } = calculateYAxis(filtered, 5);

        setMinRate(min);
        setMaxRate(max);
        setTicks(ticks);

        setRates(filtered);
      } else {
        setRates([]);
      }
    } catch (error) {
      // console.error("Error loading rates: ", error);
      setRates([]);
    } finally {
      setLoadingPeriod(false);
    }
  };

  // Выбор даты из календаря
  const handleDateSearch = async () => {
    if (!selectedDate) return;
    setLoadingToday(true);
    try {
      const data = await fetchRateByDate(selectedDate);

      if (data.currency && data.date.toString() === selectedDate.toString())
        setSelectedRate(data.currency);
      else setSelectedRate(null);
    } catch (error) {
      // console.error("Error fetching rate by date: ", error);
    } finally {
      setLoadingToday(false);
    }
  };

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setSelectedDate(today);

    const loadTodayRate = async () => {
      setLoadingToday(true);
      try {
        const data = await fetchRateByDate(today);

        if (data.currency && data.date.toString() === today.toString()) {
          setSelectedRate(data.currency);
        } else {
          setSelectedRate(null);
        }
      } catch (error) {
        // console.error("Error fetching today rate: ", error);
        setSelectedRate(null);
      } finally {
        setLoadingToday(false);
      }
    };

    loadTodayRate();
  }, []);

  useEffect(() => {
    loadRates();
  }, [limit]);

  return (
    <div className="rate-history">
      {(loadingToday || loadingPeriod) && (
        <div className="loader">
          <img src={loaderIcon} alt="loader" />
        </div>
      )}

      <div className="history-container page-width border-radius-base">
        <h3>Выберите дату, чтобы узнать курс за этот день</h3>
        <div className="flex-column column-start gap-1x w-100">
          <p className="date-title text-s">
            Внутренний курс CNY{" "}
            {selectedDate
              ? new Date(selectedDate).toLocaleDateString("ru-RU")
              : ""}
          </p>
          <p className="text-l text-accent-default">
            {loadingToday
              ? "Загрузка..."
              : selectedRate
              ? `${selectedRate} ₽`
              : "Нет данных"}
          </p>
        </div>

        <div className="controls-container flex-between w-100">
          <div className="date-input-container flex-row gap-4x">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
            <button
              className="btn-primary"
              style={{ width: 40 }}
              onClick={handleDateSearch}
            >
              <img src={arrow} alt="continue" />
            </button>
          </div>

          <div className="period-buttons flex-row gap-4x">
            <button
              className={`${limit === 7 ? "btn-primary" : "btn-secondary"}`}
              onClick={() => setLimit(7)}
            >
              Неделя
            </button>
            <button
              className={`${limit === 30 ? "btn-primary" : "btn-secondary"}`}
              onClick={() => setLimit(30)}
            >
              Месяц
            </button>
            <button
              className={`${limit === 365 ? "btn-primary" : "btn-secondary"}`}
              onClick={() => setLimit(365)}
            >
              Год
            </button>
          </div>
        </div>

        <div className="chart-wrapper">
          {loadingPeriod ? (
            <p className="text-l text-accent-default">Загрузка...</p>
          ) : rates.length === 0 ? (
            <p className="text-l text-accent-default">Нет данных</p>
          ) : (
            <ResponsiveContainer width="100%" height={288}>
              <LineChart data={rates}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) =>
                    new Date(date).toLocaleDateString("ru-RU", {
                      day: "2-digit",
                      month: "2-digit",
                    })
                  }
                  tickLine={false}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  domain={[minRate, maxRate]}
                  ticks={ticks}
                />
                <Tooltip content={<CustomTooltip />} cursor={false} />
                <Line
                  type="linear"
                  dataKey="currency"
                  stroke="#194383"
                  dot={{ fill: "#cacfd4", r: 4, stroke: "none" }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default RateHistory;
